import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { Interaction, MeetingData } from "../../models";
import Header from "../Header";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import MeetingSummary from "./MeetingSummary";
import AddInteractionDialog from "./AddInteractionDialog";
import {
  deleteInteraction,
  fetchInteractionById,
  generateActionItems,
  generateEmail,
  generateMeetingSummary,
  saveMeetingSummary,
} from "../../services/interaction.service";
import DeleteConfirmationDialog from "../Dialogs/DeleteConfirmationDialog";
import theme from "../../theme";

const InteractionDetails: React.FC = () => {
  const { interactionId } = useParams<{ interactionId: string }>();
  const [interaction, setInteraction] = useState<Interaction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [meetingData, setMeetingData] = useState<MeetingData | null>(null);
  const [processing, setProcessing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  useEffect(() => {
    fetchInteraction();
  }, [user, interactionId]);

  const fetchInteraction = async () => {
    if (!user || !interactionId) return;
    try {
      const data = await fetchInteractionById(interactionId);
      setInteraction(data);
    } catch (error) {
      console.error("Error fetching interaction:", error);
      setError("Failed to fetch interaction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === "text/plain") {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Only .txt files are supported");
    }
  };

  const handleCancelUpload = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClickOpenEditDialog = () => {
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
  };

  const handleInteractionUpdated = () => {
    setEditDialogOpen(false);
    fetchInteraction();
  };

  const handleGenerateEmail = async () => {
    setProcessing(true);
    try {
      if (file) {
        const results = await generateEmail(file);
        if (results) {
          const meetingSummary = meetingData
            ? { ...meetingData }
            : { summary: "", keyMoments: [] };
          setMeetingData({
            ...meetingSummary,
            emailTemplate: results,
          } as MeetingData);
        }
        console.log("Email template generated:", meetingData?.emailTemplate);
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setError("Failed to process file. Please try again.");
      return error;
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateActionItems = async () => {
    setProcessing(true);
    try {
      if (file) {
        const results = await generateActionItems(file);
        if (results) {
          const meetingSummary = meetingData
            ? { ...meetingData }
            : { summary: "", keyMoments: [] };
          setMeetingData({
            ...meetingSummary,
            actionItems: results,
          } as MeetingData);
        }
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setError("Failed to process file. Please try again.");
      return error;
    } finally {
      setProcessing(false);
    }
  };

  const handleGetMeetingSummary = async () => {
    setProcessing(true);
    try {
      if (file) {
        const results = await generateMeetingSummary(file);
        if (results) {
          setMeetingData(results);
        }
      }
    } catch (error) {
      console.error("Error processing file:", error);
      setError("Failed to process file. Please try again.");
      return error;
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSummary = async () => {
    if (interaction && meetingData) {
      try {
        await saveMeetingSummary(
          interaction.id,
          meetingData,
          file ? await file.text() : ""
        );
        fetchInteraction();
        setMeetingData(null);
      } catch (error) {
        console.error("Error saving to Firestore:", error);
        setError("Failed to save to Firestore. Please try again.");
      }
    }
  };

  const handleDeleteInteraction = async () => {
    if (!interactionId) return;
    try {
      await deleteInteraction(interactionId);
      setDeleteDialogOpen(false);
      navigate(-1);
    } catch (error) {
      console.error("Error deleting interaction:", error);
      setError("Failed to delete interaction. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircularProgress />
        <p>Loading interaction details...</p>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  if (!interaction) {
    return <div className="text-center">No interaction data available</div>;
  }

  return (
    <div className="min-h-screen bg-gradient text-white">
      <Header />
      <main className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg text-gray-800">
        <Box m={2} className="flex justify-between">
          <Button
            variant="contained"
            color="secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickOpenEditDialog}
            >
              Edit Interaction
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Interaction
            </Button>
          </Box>
        </Box>
        <h2 className="text-2xl font-semibold mb-4">Interaction Details</h2>
        <Paper className="p-4 mb-6">
          <p>
            <strong>Date:</strong> {interaction.date}
          </p>
          <p>
            <strong>Title:</strong> {interaction.title}
          </p>
          <p>
            <strong>Type:</strong> {interaction.type}
          </p>
          <p>
            <strong>Note:</strong> {interaction.note}
          </p>
        </Paper>
        {interaction.meetingSummary && (
          <Paper className="p-4 mb-6 border-t-4 border-blue-500">
            <h3 className="text-lg font-semibold text-gray-700">
              AI-Generated Summary
            </h3>
            <MeetingSummary
              summary={interaction.meetingSummary}
              keyMoments={interaction.keyMoments || []}
              actionItems={[]}
              emailTemplate=""
            />
          </Paper>
        )}
        <div className="mb-4">
          <input
            accept=".txt"
            type="file"
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="file-upload"
            ref={fileInputRef}
          />
          {!file && (
            <label htmlFor="file-upload">
              <Button variant="contained" color="primary" component="span">
                Upload Meeting
              </Button>
            </label>
          )}
          {file && (
            <div className="mt-4">
              <p style={{ fontWeight: "bold", marginBottom: "8px" }}>
                Selected file:{" "}
                <span style={{ color: "#3f51b5" }}>{file.name}</span>
              </p>
              <Box
                display="flex"
                flexDirection="column"
                gap={2}
                padding={2}
                borderRadius="8px"
                boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
                style={{
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Box display="flex" gap={2} justifyContent="center">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGetMeetingSummary}
                    disabled={processing}
                    size="large"
                  >
                    Generate Summary
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateActionItems}
                    disabled={processing}
                    size="large"
                  >
                    Generate Action Items
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateEmail}
                    disabled={processing}
                    size="large"
                  >
                    Generate Email
                  </Button>
                </Box>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleCancelUpload}
                    disabled={processing}
                    size="large"
                  >
                    Cancel Upload
                  </Button>
                </Box>
              </Box>
            </div>
          )}
        </div>
        {processing && (
          <div className="flex justify-center items-center mt-4">
            <CircularProgress />
            <p className="ml-4">Processing your file...</p>
          </div>
        )}
        {meetingData && !processing && (
          <Paper
            className="p-4 mt-6"
            style={{ borderTop: `4px solid ${theme.palette.secondary.main}` }}
          >
            <h3 className="text-lg font-semibold text-gray-700">
              AI Generated Data
            </h3>
            <MeetingSummary
              summary={meetingData.summary}
              keyMoments={meetingData.keyMoments}
              actionItems={meetingData.actionItems}
              emailTemplate={meetingData.emailTemplate}
            />
            <Box
              mt={4}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveSummary}
                >
                  Save to Interaction
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGetMeetingSummary}
                >
                  Regenerate Summary
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteInteraction}
        />
        <AddInteractionDialog
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
          clientId={interaction.id}
          onInteractionAdded={handleInteractionUpdated}
          interaction={interaction}
        />
      </main>
    </div>
  );
};

export default InteractionDetails;
